// DetectRTC import for reliable WebRTC feature detection
// @ts-ignore - DetectRTC doesn't have TypeScript definitions
import DetectRTC from "detectrtc";

// Utility functions for screen sharing detection

/**
 * Detects if the current device/browser supports screen sharing
 * Uses DetectRTC library for reliable detection across all platforms
 */
export function isScreenShareSupported(): boolean {
  // Try to use DetectRTC if available and already loaded
  if (typeof DetectRTC !== "undefined" && DetectRTC.isScreenCapturingSupported !== undefined) {
    const detectRTCSupported = DetectRTC.isScreenCapturingSupported;
    const hasGetDisplayMedia = !!navigator.mediaDevices?.getDisplayMedia;
    const isSecureContext = window.isSecureContext;

    console.log("=== DetectRTC Screen Sharing Detection ===");
    console.log("DetectRTC.isScreenCapturingSupported:", detectRTCSupported);
    console.log("Has getDisplayMedia API:", hasGetDisplayMedia);
    console.log("Is secure context:", isSecureContext);
    if (DetectRTC.browser) {
      console.log("Browser:", DetectRTC.browser.name, DetectRTC.browser.version);
    }
    if (DetectRTC.osName) {
      console.log("OS:", DetectRTC.osName, DetectRTC.osVersion);
    }
    console.log("Is mobile device:", DetectRTC.isMobileDevice);
    console.log("=== End DetectRTC Detection ===");

    // Primary check: DetectRTC's comprehensive detection
    if (!detectRTCSupported || !hasGetDisplayMedia || !isSecureContext) {
      return false;
    }

    // Additional checks for known limitations
    const userAgent = navigator.userAgent.toLowerCase();

    // Opera Mini definitely doesn't support it despite reporting support
    if (/opera mini/.test(userAgent)) {
      return false;
    }

    // If DetectRTC says it's supported and we have the API, trust it
    return true;
  }

  // Fallback to manual detection if DetectRTC isn't ready
  return isScreenShareSupportedSync();
}

/**
 * Async version that ensures DetectRTC is loaded before checking
 */
export async function isScreenShareSupportedAsync(): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    DetectRTC.load(() => {
      const basicSupport = DetectRTC.isScreenCapturingSupported;
      const hasGetDisplayMedia = !!navigator.mediaDevices?.getDisplayMedia;
      const isSecureContext = window.isSecureContext;

      console.log("=== Async DetectRTC Screen Sharing Detection ===");
      console.log("DetectRTC.isScreenCapturingSupported:", basicSupport);
      console.log("Has getDisplayMedia API:", hasGetDisplayMedia);
      console.log("Is secure context:", isSecureContext);
      console.log("Browser:", DetectRTC.browser.name, DetectRTC.browser.version);
      console.log("OS:", DetectRTC.osName, DetectRTC.osVersion);
      console.log("Is mobile device:", DetectRTC.isMobileDevice);
      console.log("=== End Async DetectRTC Detection ===");

      // Primary check: DetectRTC's comprehensive detection
      if (!basicSupport || !hasGetDisplayMedia || !isSecureContext) {
        resolve(false);

        return;
      }

      // Additional checks for known limitations
      const userAgent = navigator.userAgent.toLowerCase();

      // Opera Mini definitely doesn't support it despite reporting support
      if (/opera mini/.test(userAgent)) {
        resolve(false);

        return;
      }

      // If DetectRTC says it's supported and we have the API, trust it
      resolve(true);
    });
  });
}

/**
 * Synchronous version that uses manual detection as fallback
 * This is used when we need immediate results without async
 */
export function isScreenShareSupportedSync(): boolean {
  // Check basic requirements
  if (!navigator.mediaDevices || typeof navigator.mediaDevices.getDisplayMedia !== "function") {
    return false;
  }

  if (!window.isSecureContext) {
    return false;
  }

  const userAgent = navigator.userAgent.toLowerCase();

  // Desktop browsers - generally well supported
  if (!isMobileDevice()) {
    return true;
  }

  // Mobile devices require more careful detection

  // Enhanced iPadOS detection for modern devices
  const isIPadOS = detectIPadOS(userAgent);

  if (isIPadOS) {
    console.log("Screen sharing check: Detected iPadOS device");

    // iPadOS 15.4+ supports screen sharing in Safari and Chrome
    const browserInfo = detectBrowserOnIPadOS(userAgent);

    console.log("Screen sharing check: Browser info:", browserInfo);

    if (browserInfo.isSafari || browserInfo.isChrome) {
      // Try to detect iPadOS version
      const versionInfo = detectIPadOSVersion(userAgent);

      console.log("Screen sharing check: Version info:", versionInfo);

      if (versionInfo.detected) {
        const supported =
          versionInfo.majorVersion > 15 ||
          (versionInfo.majorVersion === 15 && versionInfo.minorVersion >= 4);

        console.log(
          `Screen sharing check: iPadOS ${versionInfo.versionString} - Supported: ${supported}`,
        );

        return supported;
      }

      // If we can't detect version but have the API and it's a modern browser, assume it works
      // Modern iPads (A16, M1, M2 chips) with recent iPadOS should definitely work
      console.log(
        "Screen sharing check: Version detection failed, but API exists and browser supported - assuming support for modern iPadOS",
      );

      return true;
    }

    // Additional fallback for Chrome that might not be detected by the main pattern
    // Some Chrome versions on iPadOS might have different user agent strings
    if (/chrome/.test(userAgent) && !browserInfo.isChrome) {
      console.log("Screen sharing check: Chrome detected via fallback pattern");

      return true;
    }

    console.log(
      `Screen sharing check: Browser ${browserInfo.browserName} not supported for screen sharing on iPadOS`,
    );

    return false;
  }

  // iPhone/iPod detection
  const isIPhone = /iphone|ipod/.test(userAgent);

  if (isIPhone) {
    // iPhone has limited screen sharing support, mainly for app content
    const isSafari = /safari/.test(userAgent) && !/chrome|crios|fxios/.test(userAgent);
    const isChrome = /crios/.test(userAgent);

    if (isSafari || isChrome) {
      const versionMatch = userAgent.match(/os (\d+)_(\d+)/);

      if (versionMatch) {
        const majorVersion = parseInt(versionMatch[1]);
        const minorVersion = parseInt(versionMatch[2]);

        // iOS 15.4+ has some support, but still limited
        return majorVersion > 15 || (majorVersion === 15 && minorVersion >= 4);
      }
    }

    return false;
  }

  // Android detection - more permissive approach
  const isAndroid = /android/.test(userAgent);

  if (isAndroid) {
    // Modern browsers on Android generally support screen sharing
    const isChromeAndroid = /chrome/.test(userAgent);
    const isFirefoxAndroid = /firefox/.test(userAgent);
    const isEdgeAndroid = /edg/.test(userAgent);
    const isSamsungBrowser = /samsungbrowser/.test(userAgent);

    // Major browsers with known support
    if (isChromeAndroid || isFirefoxAndroid || isEdgeAndroid || isSamsungBrowser) {
      return true;
    }

    // For other browsers, if they have the API, let them try
    // This is more permissive than before
    return true;
  }

  // Other mobile devices (BlackBerry, Windows Mobile, etc.)
  if (/blackberry|iemobile|opera mini|webos/.test(userAgent)) {
    return false;
  }

  // If we have the API and it's not a known problematic device, allow it
  return true;
}

/**
 * Detects if the current device is a mobile device
 */
export function isMobileDevice(): boolean {
  const userAgent = navigator.userAgent.toLowerCase();

  return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
}

/**
 * Tests actual screen sharing capability by checking browser features
 * This provides a more reliable test than user agent detection alone
 */
export async function testScreenShareCapability(): Promise<boolean> {
  if (!isScreenShareSupported()) {
    return false;
  }

  try {
    // Check if the API is properly implemented
    const mediaDevices = navigator.mediaDevices as any;

    // Verify getDisplayMedia is a function
    if (typeof mediaDevices.getDisplayMedia !== "function") {
      return false;
    }

    // On some browsers, we can check getSupportedConstraints
    if (typeof mediaDevices.getSupportedConstraints === "function") {
      const supportedConstraints = mediaDevices.getSupportedConstraints();

      // Check if display-related constraints are supported
      if (supportedConstraints.displaySurface !== undefined) {
        return true;
      }
    }

    // Check if we're in a secure context (required for getDisplayMedia)
    if (typeof window !== "undefined" && !window.isSecureContext) {
      console.warn("Screen sharing requires a secure context (HTTPS)");

      return false;
    }

    // Additional check for certain browsers that report false support
    const userAgent = navigator.userAgent.toLowerCase();

    // Opera Mini definitely doesn't support it despite having the API
    if (/opera mini/.test(userAgent)) {
      return false;
    }

    // We can't actually test getDisplayMedia without user interaction,
    // but if we've passed all checks, it's likely to work
    return true;
  } catch (error) {
    console.warn("Screen sharing capability test failed:", error);

    return false;
  }
}

/**
 * Gets a user-friendly message explaining why screen sharing isn't available
 */
export function getScreenShareUnsupportedReason(): string {
  const userAgent = navigator.userAgent.toLowerCase();

  if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
    return "Screen sharing is not supported in this browser";
  }

  // iPadOS detection
  const isIPadOS =
    /ipad/.test(userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  if (isIPadOS) {
    return "Screen sharing requires iPadOS 15.4+ and Safari or Chrome";
  }

  if (/iphone|ipod/.test(userAgent)) {
    return "Screen sharing has limited support on iPhone (iOS 15.4+ required)";
  }

  if (/android/.test(userAgent)) {
    return "Screen sharing requires a modern browser like Chrome, Firefox, or Edge";
  }

  if (/blackberry|iemobile|opera mini|webos/.test(userAgent)) {
    return "Screen sharing is not supported on this mobile platform";
  }

  return "Screen sharing is not supported on this device";
}

/**
 * Gets device-specific information about screen sharing capabilities
 */
export function getScreenShareInfo(): {
  supported: boolean;
  reason?: string;
  limitations?: string[];
  browserRecommendation?: string;
} {
  const userAgent = navigator.userAgent.toLowerCase();
  const isSupported = isScreenShareSupported();

  if (!isSupported) {
    return {
      supported: false,
      reason: getScreenShareUnsupportedReason(),
    };
  }

  const limitations: string[] = [];
  let browserRecommendation: string | undefined;

  // iPadOS
  const isIPadOS =
    /ipad/.test(userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  if (isIPadOS) {
    limitations.push("Limited to app content and browser tabs");
    limitations.push("Cannot share entire screen");
    if (!/safari|chrome/.test(userAgent)) {
      browserRecommendation = "For best results, use Safari or Chrome";
    }
  }

  // iPhone
  if (/iphone|ipod/.test(userAgent)) {
    limitations.push("Very limited support - mainly for app content");
    limitations.push("May not work in all scenarios");
    browserRecommendation = "Consider using a desktop or tablet for screen sharing";
  }

  // Android
  if (/android/.test(userAgent)) {
    if (!/chrome|firefox|edg|samsungbrowser/.test(userAgent)) {
      browserRecommendation = "For best results, use Chrome, Firefox, or Edge";
    }
    limitations.push("Performance may vary by device");
  }

  return {
    supported: true,
    limitations: limitations.length > 0 ? limitations : undefined,
    browserRecommendation,
  };
}

/**
 * Enhanced iPadOS detection that handles modern iPad devices
 * Modern iPads often report as macOS Safari, so we need multiple detection methods
 */
function detectIPadOS(userAgent: string): boolean {
  // Direct iPad detection in user agent
  if (/ipad/.test(userAgent)) {
    return true;
  }

  // Modern iPad detection: macOS Safari with touch support
  if (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) {
    return true;
  }

  // Additional check for very new iPads that might report differently
  if (navigator.platform === "MacIntel" && "ontouchend" in document) {
    return true;
  }

  // Check for iPad-specific capabilities
  if (
    /macintosh/.test(userAgent) &&
    navigator.maxTouchPoints > 1 &&
    typeof DeviceMotionEvent !== "undefined"
  ) {
    return true;
  }

  return false;
}

/**
 * Detect browser type on iPadOS
 */
function detectBrowserOnIPadOS(userAgent: string): {
  isSafari: boolean;
  isChrome: boolean;
  browserName: string;
} {
  // Safari detection (default browser)
  const isSafari = /safari/.test(userAgent) && !/chrome|crios|fxios/.test(userAgent);

  // Enhanced Chrome detection for iPadOS
  // Chrome on iPadOS can appear as:
  // 1. CriOS (Chrome iOS app)
  // 2. Chrome (desktop mode or mobile mode)
  const isChrome = /crios/.test(userAgent) || /chrome/.test(userAgent);

  let browserName = "unknown";

  if (isSafari) browserName = "Safari";
  else if (isChrome) browserName = "Chrome";
  else if (/fxios/.test(userAgent)) browserName = "Firefox";
  else if (/edgios/.test(userAgent)) browserName = "Edge";

  return { isSafari, isChrome, browserName };
}

/**
 * Detect iPadOS version with multiple fallback methods
 */
function detectIPadOSVersion(userAgent: string): {
  detected: boolean;
  majorVersion: number;
  minorVersion: number;
  versionString: string;
} {
  // Try different version detection patterns

  // Pattern 1: OS version in user agent (os 18_5 format)
  let versionMatch = userAgent.match(/os (\d+)_(\d+)/);

  if (versionMatch) {
    return {
      detected: true,
      majorVersion: parseInt(versionMatch[1]),
      minorVersion: parseInt(versionMatch[2]),
      versionString: `${versionMatch[1]}.${versionMatch[2]}`,
    };
  }

  // Pattern 2: Version in different format (18.5 format)
  versionMatch = userAgent.match(/version\/(\d+)\.(\d+)/);

  if (versionMatch) {
    return {
      detected: true,
      majorVersion: parseInt(versionMatch[1]),
      minorVersion: parseInt(versionMatch[2]),
      versionString: `${versionMatch[1]}.${versionMatch[2]}`,
    };
  }

  // Pattern 3: macOS version when iPad reports as Mac (might indicate newer iPadOS)
  versionMatch = userAgent.match(/mac os x (\d+)_(\d+)/);

  if (versionMatch) {
    // If iPad is reporting as macOS, it's likely a newer iPadOS version
    const macMajor = parseInt(versionMatch[1]);
    const macMinor = parseInt(versionMatch[2]);

    // Estimate iPadOS version based on macOS version
    // This is approximate since iPads sometimes report higher macOS versions
    let iPadOSMajor = macMajor >= 12 ? 18 : 15; // Assume modern if high macOS version

    return {
      detected: true,
      majorVersion: iPadOSMajor,
      minorVersion: macMinor,
      versionString: `${iPadOSMajor}.${macMinor} (estimated from macOS ${macMajor}.${macMinor})`,
    };
  }

  return {
    detected: false,
    majorVersion: 0,
    minorVersion: 0,
    versionString: "unknown",
  };
}

/**
 * Debug function to test screen sharing detection - can be called from browser console
 * Usage: testScreenShareDetection()
 */
(window as any).testScreenShareDetection = function () {
  const userAgent = navigator.userAgent.toLowerCase();

  console.log("=== Screen Share Detection Debug ===");
  console.log("User Agent:", navigator.userAgent);
  console.log("Platform:", navigator.platform);
  console.log("Max Touch Points:", navigator.maxTouchPoints);
  console.log("Has getDisplayMedia:", !!navigator.mediaDevices?.getDisplayMedia);
  console.log("Is Secure Context:", window.isSecureContext);
  console.log("Device Motion Available:", typeof DeviceMotionEvent !== "undefined");
  console.log("Touch End Support:", "ontouchend" in document);

  // Test with DetectRTC
  console.log("\n--- DetectRTC Detection ---");
  console.log("DetectRTC available:", typeof DetectRTC !== "undefined");

  if (typeof DetectRTC !== "undefined") {
    DetectRTC.load(() => {
      console.log("DetectRTC.isScreenCapturingSupported:", DetectRTC.isScreenCapturingSupported);
      console.log("DetectRTC.browser:", DetectRTC.browser);
      console.log("DetectRTC.osName:", DetectRTC.osName);
      console.log("DetectRTC.osVersion:", DetectRTC.osVersion);
      console.log("DetectRTC.isMobileDevice:", DetectRTC.isMobileDevice);
    });
  } else {
    console.log("DetectRTC not available, falling back to manual detection");
  }

  console.log("\n--- iPadOS Detection ---");
  const isIPadOS = detectIPadOS(userAgent);

  console.log("Is iPadOS:", isIPadOS);

  if (isIPadOS) {
    const browserInfo = detectBrowserOnIPadOS(userAgent);

    console.log("Browser Info:", browserInfo);

    const versionInfo = detectIPadOSVersion(userAgent);

    console.log("Version Info:", versionInfo);
  }

  console.log("\n--- Final Result ---");
  const supportedSync = isScreenShareSupportedSync();

  console.log("Screen Sharing Supported (Sync):", supportedSync);

  // Test async version
  isScreenShareSupportedAsync().then((supportedAsync) => {
    console.log("Screen Sharing Supported (Async):", supportedAsync);
  });

  if (!supportedSync) {
    console.log("Reason:", getScreenShareUnsupportedReason());
  }

  const info = getScreenShareInfo();

  console.log("Detailed Info:", info);

  console.log("=== End Debug ===");

  return {
    supportedSync,
    isIPadOS,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    maxTouchPoints: navigator.maxTouchPoints,
    hasGetDisplayMedia: !!navigator.mediaDevices?.getDisplayMedia,
    isSecureContext: window.isSecureContext,
  };
};

/**
 * Force enable screen sharing for testing purposes
 * Usage: forceEnableScreenShare()
 */
(window as any).forceEnableScreenShare = function () {
  console.log("🚀 Force enabling screen sharing for testing...");

  (window as any).isScreenShareSupported = () => {
    console.log("🔧 Force enabled screen sharing detected as supported");

    return true;
  };

  console.log("✅ Screen sharing force enabled! Refresh the page to revert.");
  console.log("💡 Now the screen share button should appear regardless of device detection.");

  return true;
};

/**
 * Test actual screen sharing capability by trying to invoke getDisplayMedia
 * This will prompt the user and test real functionality
 * Usage: testActualScreenShare()
 */
(window as any).testActualScreenShare = function () {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
    console.error("❌ getDisplayMedia not available");

    return Promise.reject("getDisplayMedia not available");
  }

  console.log("🎬 Testing actual screen sharing capability...");

  return navigator.mediaDevices
    .getDisplayMedia({
      video: true,
      audio: false,
    })
    .then((stream) => {
      console.log("✅ Screen sharing works! Stream:", stream);

      // Stop the stream immediately to cleanup
      stream.getTracks().forEach((track) => track.stop());

      console.log("🛑 Stream stopped. Screen sharing is definitely supported on this device!");

      return true;
    })
    .catch((error) => {
      console.error("❌ Screen sharing failed:", error);

      return false;
    });
};

/**
 * Debug function specifically for testing iPadOS Chrome detection
 * Usage: testIPadOSChromeDetection()
 */
(window as any).testIPadOSChromeDetection = function () {
  const userAgent = navigator.userAgent.toLowerCase();

  console.log("=== iPadOS Chrome Detection Test ===");
  console.log("User Agent:", navigator.userAgent);
  console.log("Lowercase UA:", userAgent);
  console.log("Platform:", navigator.platform);
  console.log("Max Touch Points:", navigator.maxTouchPoints);

  // Test iPadOS detection
  const isIPadOS = detectIPadOS(userAgent);

  console.log("🔍 Is iPadOS:", isIPadOS);

  if (isIPadOS) {
    // Test browser detection
    const browserInfo = detectBrowserOnIPadOS(userAgent);

    console.log("🌐 Browser Info:", browserInfo);

    // Test version detection
    const versionInfo = detectIPadOSVersion(userAgent);

    console.log("📱 Version Info:", versionInfo);

    // Test individual patterns
    console.log("\n--- Pattern Tests ---");
    console.log("Has 'crios':", /crios/.test(userAgent));
    console.log("Has 'chrome':", /chrome/.test(userAgent));
    console.log("Has 'safari':", /safari/.test(userAgent));
    console.log("Has 'mobile':", /mobile/.test(userAgent));

    // Test final result
    const shouldSupport =
      (browserInfo.isSafari || browserInfo.isChrome) &&
      (versionInfo.majorVersion > 15 ||
        (versionInfo.majorVersion === 15 && versionInfo.minorVersion >= 4));

    console.log("🎯 Should Support Screen Share:", shouldSupport);

    // Test with DetectRTC if available
    if (typeof DetectRTC !== "undefined") {
      console.log("\n--- DetectRTC Test ---");
      DetectRTC.load(() => {
        console.log("DetectRTC.isScreenCapturingSupported:", DetectRTC.isScreenCapturingSupported);
        console.log("DetectRTC.browser:", DetectRTC.browser);
        console.log("DetectRTC.isMobileDevice:", DetectRTC.isMobileDevice);
      });
    }
  }

  console.log("=== End iPadOS Chrome Test ===");

  return {
    isIPadOS,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    maxTouchPoints: navigator.maxTouchPoints,
  };
};

/**
 * Comprehensive test specifically for iPad A16 with Chrome
 * Usage: testIPadA16Chrome()
 */
(window as any).testIPadA16Chrome = function () {
  console.log("=== iPad A16 Chrome Screen Sharing Test ===");

  const userAgent = navigator.userAgent.toLowerCase();
  const originalUA = navigator.userAgent;

  console.log("🔍 Device Info:");
  console.log("- User Agent:", originalUA);
  console.log("- Platform:", navigator.platform);
  console.log("- Max Touch Points:", navigator.maxTouchPoints);
  console.log("- Has getDisplayMedia:", !!navigator.mediaDevices?.getDisplayMedia);
  console.log("- Is Secure Context:", window.isSecureContext);

  console.log("\n🧪 Detection Tests:");

  // Test 1: Basic API check
  const hasAPI = !!navigator.mediaDevices?.getDisplayMedia;

  console.log("✓ Has getDisplayMedia API:", hasAPI);

  // Test 2: iPadOS detection
  const isIPadOS = detectIPadOS(userAgent);

  console.log("✓ iPadOS detected:", isIPadOS);

  // Test 3: Browser detection
  const browserInfo = detectBrowserOnIPadOS(userAgent);

  console.log("✓ Browser info:", browserInfo);

  // Test 4: Version detection
  const versionInfo = detectIPadOSVersion(userAgent);

  console.log("✓ Version info:", versionInfo);

  // Test 5: Individual pattern matches
  console.log("\n🔍 Pattern Matches:");
  console.log("- Contains 'ipad':", /ipad/.test(userAgent));
  console.log("- Contains 'chrome':", /chrome/.test(userAgent));
  console.log("- Contains 'crios':", /crios/.test(userAgent));
  console.log("- Contains 'safari':", /safari/.test(userAgent));
  console.log(
    "- Platform MacIntel + Touch:",
    navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1,
  );

  // Test 6: Current detection results
  const syncResult = isScreenShareSupportedSync();

  console.log("\n📊 Detection Results:");
  console.log("- Sync detection:", syncResult);

  // Test 7: DetectRTC async test
  console.log("- Testing async DetectRTC...");
  isScreenShareSupportedAsync().then((asyncResult) => {
    console.log("- Async detection:", asyncResult);

    // Final recommendation
    console.log("\n🎯 Final Analysis:");
    if (hasAPI && isIPadOS && (browserInfo.isChrome || /chrome/.test(userAgent))) {
      console.log("✅ Your iPad A16 with Chrome SHOULD support screen sharing!");
      console.log("💡 If the button doesn't appear, there might be a detection bug.");
      console.log("🔧 Try running: forceEnableScreenShare()");
    } else {
      console.log("❌ Detection indicates no support, but this might be incorrect.");
      console.log("🔧 Try running: testActualScreenShare() to test real functionality");
    }
  });

  console.log("=== End iPad A16 Chrome Test ===");

  return {
    hasAPI,
    isIPadOS,
    browserInfo,
    versionInfo,
    syncResult,
    userAgent: originalUA,
    platform: navigator.platform,
    maxTouchPoints: navigator.maxTouchPoints,
  };
};
